import React, { useCallback, useEffect, useRef, useState } from 'react';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import {
  strapiDownloadWorkOrderLhuPdf,
  strapiOpenWorkOrderLhuPdfHtmlPreview,
  strapiSendWorkOrderLhuPdf,
} from '../../api/strapiCrm';

const LHU_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeLhuEmails(values: string[]): { emails: string[]; invalid: string[] } {
  const emails: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    for (const part of String(value).split(/[,;\s]+/)) {
      const email = part.trim();
      if (!email) continue;
      if (!LHU_EMAIL_RE.test(email)) {
        if (!invalid.includes(email)) invalid.push(email);
        continue;
      }
      const key = email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      emails.push(email);
    }
  }
  return { emails, invalid };
}

type Props = {
  open: boolean;
  workOrderDocumentId: string | null;
  workOrderLabel?: string | null;
  jwt: string | null;
  /** Tampilkan tombol kirim PDF LHU ke email customer (Customer Service). */
  canSendCustomerEmail?: boolean;
  /** Email customer LHU. Kosong berarti input email bebas diisi. */
  customerEmail?: string | null;
  onClose: () => void;
};

export function LhuPdfPreviewDialog({
  open,
  workOrderDocumentId,
  workOrderLabel,
  jwt,
  canSendCustomerEmail = false,
  customerEmail,
  onClose,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [openingHtml, setOpeningHtml] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmails, setSendEmails] = useState<string[]>([]);
  const [sendEmailInput, setSendEmailInput] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const defaultCustomerEmail = customerEmail?.trim() ?? '';

  const applySendEmails = useCallback((values: string[]) => {
    const { emails, invalid } = normalizeLhuEmails(values);
    setSendEmails(emails);
    setSendError(invalid.length ? `Alamat email tidak valid: ${invalid.join(', ')}` : null);
    return { emails, invalid };
  }, []);

  const sendM = useMutation({
    mutationFn: async (emails: string[]) => {
      if (!jwt) throw new Error('Sesi login tidak ada.');
      if (!workOrderDocumentId) throw new Error('Work order tidak ditemukan.');
      const { emails: recipients, invalid } = normalizeLhuEmails(emails);
      if (!recipients.length || invalid.length) throw new Error('Alamat email tidak valid.');
      await strapiSendWorkOrderLhuPdf(jwt, workOrderDocumentId, recipients);
      return recipients;
    },
    onSuccess: (recipients) => {
      setSendOpen(false);
      setSendEmailInput('');
      setSendError(null);
      setSendSuccess(`PDF LHU terkirim ke ${recipients.join(', ')}.`);
    },
    onError: (e) => {
      setSendError(e instanceof Error ? e.message : 'Gagal mengirim PDF LHU.');
    },
  });

  const loadPdf = useCallback(
    async (opts?: { keepPreview?: boolean }) => {
      if (!workOrderDocumentId || !jwt) return;
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      if (!opts?.keepPreview) {
        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      }
      try {
        const blob = await strapiDownloadWorkOrderLhuPdf(jwt, workOrderDocumentId, {
          cacheBust: Boolean(opts?.keepPreview),
        });
        if (requestId !== requestIdRef.current) return;
        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      } catch (e) {
        if (requestId !== requestIdRef.current) return;
        setError(e instanceof Error ? e.message : 'Gagal memuat PDF LHU.');
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [jwt, workOrderDocumentId],
  );

  useEffect(() => {
    if (!open || !workOrderDocumentId || !jwt) return undefined;
    void loadPdf();
    return undefined;
  }, [open, workOrderDocumentId, jwt, loadPdf]);

  useEffect(() => {
    if (open) return undefined;
    requestIdRef.current += 1;
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setError(null);
    setLoading(false);
    setRegenerating(false);
    setOpeningHtml(false);
    setSendOpen(false);
    setSendEmails([]);
    setSendEmailInput('');
    setSendError(null);
    setSendSuccess(null);
    return undefined;
  }, [open]);

  async function handleRegenerate() {
    if (!workOrderDocumentId || !jwt || loading || regenerating) return;
    setRegenerating(true);
    try {
      await loadPdf({ keepPreview: true });
    } finally {
      setRegenerating(false);
    }
  }

  async function handleOpenHtmlPreview() {
    if (!workOrderDocumentId || !jwt || loading || regenerating || openingHtml) return;
    setOpeningHtml(true);
    setError(null);
    try {
      await strapiOpenWorkOrderLhuPdfHtmlPreview(jwt, workOrderDocumentId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal membuka preview HTML LHU.');
    } finally {
      setOpeningHtml(false);
    }
  }

  function handleDownload() {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    const safe = (workOrderLabel?.trim() || workOrderDocumentId || 'LHU').replace(/[^\w.-]+/g, '_');
    a.download = `${safe}_DRAFT_LHU.pdf`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const titleLabel = workOrderLabel?.trim() || workOrderDocumentId || 'LHU';
  const pdfBusy = loading || regenerating;
  const busy = pdfBusy || openingHtml;
  const sendDraft = normalizeLhuEmails([...sendEmails, sendEmailInput]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        aria-labelledby="invoice-lhu-pdf-preview-title"
        data-testid="invoice-lhu-pdf-preview-dialog"
      >
        <DialogTitle id="invoice-lhu-pdf-preview-title" sx={{ pr: 6 }}>
          Draft LHU — {titleLabel}
          <IconButton
            aria-label="Tutup"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: '20px',
            height: { xs: '70vh', md: '80vh' },
            bgcolor: 'grey.100',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {sendSuccess ? (
            <Alert severity="success" onClose={() => setSendSuccess(null)} sx={{ mb: 2, flexShrink: 0 }}>
              {sendSuccess}
            </Alert>
          ) : null}
          {error ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : null}
          {!error && pdfBusy && !pdfUrl ? (
            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', gap: 2, p: 3 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                {regenerating ? 'Menggenerate ulang PDF LHU…' : 'Menyiapkan PDF LHU…'}
              </Typography>
            </Stack>
          ) : null}
          {!error && pdfUrl ? (
            <Box sx={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
              {pdfBusy ? (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 2,
                    bgcolor: 'rgba(255,255,255,0.72)',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Menggenerate ulang PDF LHU…
                  </Typography>
                </Stack>
              ) : null}
              <Box
                component="iframe"
                src={pdfUrl}
                title={`PDF LHU — ${titleLabel}`}
                sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={() => void handleRegenerate()}
            disabled={!workOrderDocumentId || !jwt || busy}
            startIcon={regenerating ? <CircularProgress size={16} color="inherit" /> : <AutorenewOutlinedIcon />}
          >
            Generate ulang
          </Button>
          <Button
            onClick={() => void handleOpenHtmlPreview()}
            disabled={!workOrderDocumentId || !jwt || busy}
            startIcon={openingHtml ? <CircularProgress size={16} color="inherit" /> : <OpenInNewOutlinedIcon />}
          >
            Preview HTML
          </Button>
          {canSendCustomerEmail ? (
            <Button
              onClick={() => {
                setSendError(null);
                setSendSuccess(null);
                setSendEmailInput('');
                const preset = defaultCustomerEmail.trim();
                setSendEmails(LHU_EMAIL_RE.test(preset) ? [preset] : []);
                setSendOpen(true);
              }}
              disabled={!workOrderDocumentId || !jwt || busy || sendM.isPending}
              startIcon={
                sendM.isPending ? <CircularProgress size={16} color="inherit" /> : <ForwardToInboxOutlinedIcon />
              }
              data-testid="invoice-lhu-pdf-send-customer-email"
            >
              Kirim email customer
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleDownload} disabled={!pdfUrl || busy} startIcon={<DownloadOutlinedIcon />}>
            Unduh PDF
          </Button>
          <Button onClick={onClose}>Tutup</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sendOpen}
        onClose={() => {
          if (sendM.isPending) return;
          setSendOpen(false);
          setSendEmailInput('');
          setSendError(null);
        }}
        maxWidth="sm"
        fullWidth
        data-testid="invoice-lhu-pdf-send-customer-email-dialog"
      >
        <DialogTitle>Kirim PDF LHU ke customer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            PDF LHU {titleLabel} akan dikirim sebagai lampiran email.
          </Typography>
          <Autocomplete<string, true, false, true>
            multiple
            freeSolo
            autoSelect
            open={false}
            forcePopupIcon={false}
            options={[]}
            value={sendEmails}
            inputValue={sendEmailInput}
            onInputChange={(_, next, reason) => {
              if (reason !== 'input') return;
              if (!/[,;\s]/.test(next)) {
                setSendEmailInput(next);
                return;
              }
              const endsWithSeparator = /[,;\s]$/.test(next);
              const parts = next.split(/[,;\s]+/).map((part) => part.trim()).filter(Boolean);
              const rest = endsWithSeparator ? '' : (parts.pop() ?? '');
              const committed = rest && LHU_EMAIL_RE.test(rest) ? [...parts, rest] : parts;
              applySendEmails([...sendEmails, ...committed]);
              setSendEmailInput(rest && !LHU_EMAIL_RE.test(rest) ? rest : '');
            }}
            onChange={(_, next) => {
              applySendEmails(next.map(String));
              setSendEmailInput('');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email"
                fullWidth
                required
                autoFocus
                placeholder={sendEmails.length ? '' : 'nama@perusahaan.com'}
                helperText={
                  defaultCustomerEmail
                    ? 'Diisi dari email customer pada LHU ini. Ketik email lain lalu tekan Enter, koma, atau spasi.'
                    : 'Customer tidak punya email. Ketik alamat lalu tekan Enter, koma, atau spasi.'
                }
                data-testid="invoice-lhu-pdf-send-customer-email-input"
                inputProps={{
                  ...params.inputProps,
                  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    const draft = event.currentTarget.value.trim();
                    if ((event.key === 'Enter' || event.key === ',' || event.key === ' ') && draft) {
                      event.preventDefault();
                      event.stopPropagation();
                      applySendEmails([...sendEmails, draft]);
                      setSendEmailInput('');
                      return;
                    }
                    params.inputProps.onKeyDown?.(event);
                  },
                }}
              />
            )}
          />
          {sendError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {sendError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (sendM.isPending) return;
              setSendOpen(false);
              setSendEmailInput('');
              setSendError(null);
            }}
            disabled={sendM.isPending}
          >
            Batal
          </Button>
          <Button
            variant="contained"
            disabled={sendM.isPending || sendDraft.emails.length === 0}
            onClick={() => {
              const { emails, invalid } = applySendEmails([...sendEmails, sendEmailInput]);
              if (invalid.length || !emails.length) return;
              setSendEmailInput('');
              sendM.mutate(emails);
            }}
            data-testid="invoice-lhu-pdf-send-customer-email-submit"
          >
            {sendM.isPending ? 'Mengirim…' : 'Kirim'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
